import * as React from 'react';
import { memo, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Spin, Alert, Empty } from 'antd';
import { RightOutlined, RedoOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { PATHS } from 'router/config';
import { ZoomStatus } from 'types';
import {
  useRecentlyMatched,
  useZoomMutations,
  RecentMatchRow,
} from 'core/hooks';
import { formatWarsaw } from 'core/utils/zoomTime';
import { ZoomStatusBadge, JournalLinkButton } from 'molecules';
import { Colors } from 'services/ColorService';
import { getFont } from 'services/FontService';

const REFETCH_DELAY = 3_000;

/** Statuses that block the «process» (retry) action — mirrors LessonsList. */
const RETRY_BLOCKED: ZoomStatus[] = [
  ZoomStatus.DOWNLOADING,
  ZoomStatus.UPLOADED,
  ZoomStatus.AI_PROCESSING,
  ZoomStatus.SKIPPED,
];

/**
 * Maps a LessonRecording `status` string to the page-level `ZoomStatus` enum —
 * the same cast `lessonStatus` performs internally for lesson-centric rows.
 */
const recordingStatus = (row: RecentMatchRow): ZoomStatus =>
  (row.status as unknown) as ZoomStatus;

export const RecentList = memo(() => {
  const { t } = useTranslation();
  const history = useHistory();

  const [mineOnly, setMineOnly] = useState(true);

  const { rows, loading, error, refetch } = useRecentlyMatched(mineOnly, 20);
  const mutations = useZoomMutations();

  const refetchSoon = useCallback(() => {
    window.setTimeout(refetch, REFETCH_DELAY);
  }, [refetch]);

  const openLesson = useCallback(
    (id: string) => history.push(`${PATHS.zoomLessons}/${id}`),
    [history],
  );

  const renderItem = (row: RecentMatchRow) => {
    const { lesson } = row;
    const group = lesson?.module?.group;
    const course = group?.course;
    const st = recordingStatus(row);
    const retryBlocked = RETRY_BLOCKED.includes(st) || mutations.retry.loading;

    return (
      <Card
        key={row.id}
        onClick={() => (lesson ? openLesson(lesson.id) : undefined)}
      >
        <CardTop>
          <TitleBlock>
            <Title>{lesson?.name}</Title>
            <Subtitle>
              {group?.name}
              {course?.name ? ` · ${course.name}` : ''}
            </Subtitle>
          </TitleBlock>
          <StatusBlock>
            <ZoomStatusBadge status={st} />
            <Chevron>
              <RightOutlined />
            </Chevron>
          </StatusBlock>
        </CardTop>

        <MetaRow>
          <MetaItem>
            {t('pages.zoom.recent.matchedBy', {
              email: row.matchedByUser?.email ?? '—',
            })}
          </MetaItem>
          <MetaItem>{formatWarsaw(row.createdAt)}</MetaItem>
        </MetaRow>

        <Divider />

        <Actions onClick={(event: React.MouseEvent) => event.stopPropagation()}>
          {lesson ? (
            <Button onClick={() => openLesson(lesson.id)}>
              {t('pages.zoom.recent.goToLesson')}
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<RedoOutlined />}
            disabled={retryBlocked}
            onClick={async () => {
              await mutations.retry.run(row.id);
              refetchSoon();
            }}
          >
            {t('pages.zoom.recent.process')}
          </Button>
          <Spacer />
          {group?.id ? <JournalLinkButton groupId={group.id} /> : null}
        </Actions>
      </Card>
    );
  };

  return (
    <Page>
      <PageHeader>
        <HeaderTitleRow>
          <HeaderTitle>{t('pages.zoom.recent.title')}</HeaderTitle>
          <HeaderCount>
            {t('pages.zoom.recent.count', { count: rows.length })}
          </HeaderCount>
        </HeaderTitleRow>
        <HeaderSubtitle>{t('pages.zoom.recent.subtitle')}</HeaderSubtitle>
      </PageHeader>

      <FilterCard>
        <Checkbox
          checked={mineOnly}
          onChange={(event) => setMineOnly(event.target.checked)}
        >
          {t('pages.zoom.recent.mineOnly')}
        </Checkbox>
      </FilterCard>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {loading && !rows.length ? (
        <CenteredPanel>
          <Spin />
        </CenteredPanel>
      ) : null}

      {!loading && !rows.length ? (
        <CenteredPanel>
          <Empty description={t('pages.zoom.recent.empty')} />
        </CenteredPanel>
      ) : null}

      {rows.length ? <List>{rows.map(renderItem)}</List> : null}
    </Page>
  );
});

const Page = styled.div`
  padding: 16px 0 40px;
`;

const PageHeader = styled.div`
  margin-bottom: 16px;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.h2`
  ${getFont({ fontSize: 22, lineHeight: 28, familyWeight: 'bold' })};
  color: ${Colors.Black};
  margin: 0;
`;

const HeaderCount = styled.span`
  background: ${Colors.BasicBlueLight};
  color: ${Colors.Blue};
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 13px;
`;

const HeaderSubtitle = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${Colors.Gray};
`;

const FilterCard = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;

const CenteredPanel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px ${Colors.BlueShadow};
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${Colors.Blue};
    box-shadow: 0 6px 20px ${Colors.BlueShadowRow};
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.div`
  ${getFont({ fontSize: 16, lineHeight: 22, familyWeight: 'semibold' })};
  color: ${Colors.Blue};

  ${Card}:hover & {
    text-decoration: underline;
  }
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: ${Colors.Gray};
  margin-top: 2px;
`;

const StatusBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const Chevron = styled.span`
  color: ${Colors.Gray};
  font-size: 16px;
  display: inline-flex;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 12px;
  font-size: 13px;
  color: ${Colors.Gray};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${Colors.Gray};
`;

const Divider = styled.div`
  height: 1px;
  background: ${Colors.LightGray};
  margin: 14px 0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;
